import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiX, FiMic } from 'react-icons/fi'
import LyoAvatar from '../avatar/LyoAvatar'
import { useMood } from '../../contexts/MoodContext'
import { useChat } from '../../contexts/ChatContext'

const VoiceChatModal = ({ isOpen, onClose, onVoiceMessage }) => {
  const { mood } = useMood()
  const { detectedLanguage } = useChat()

  const formatLanguage = (lang) => {
    const languages = {
      english: 'English',
      tagalog: 'Tagalog',
      spanish: 'Spanish',
      french: 'French',
      german: 'German',
      italian: 'Italian',
      portuguese: 'Portuguese',
      japanese: 'Japanese',
      korean: 'Korean',
      chinese: 'Chinese'
    }
    return languages[lang] || lang.charAt(0).toUpperCase() + lang.slice(1)
  }

  const handleClose = () => {
    console.log('Closing voice chat modal')
    onClose()
  }
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleClose()
            }
          }}
        >
          <motion.div
            className="glass-panel p-8 rounded-3xl max-w-md w-full mx-4 text-center"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center">
                <h2 className="text-xl font-display text-white mr-3">Voice Chat</h2>
                <div className="flex items-center text-sm text-gray-400">
                  <span>{formatLanguage(detectedLanguage)}</span>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>
            
            {/* Avatar */}
            <div className="relative h-48 w-48 mx-auto mb-6">
              <LyoAvatar 
                mood={mood} 
                isListening={false}
                isSpeaking={false}
                emotion="neutral"
              />
            </div>
            
            {/* Coming Soon Message */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl font-display text-neon-cyan mb-4">Coming Soon</h3>
              <p className="text-gray-300 text-lg mb-2">Voice chat functionality</p>
              <p className="text-gray-400 text-sm">
                We're working on bringing you seamless voice conversations with Lyo. 
                Stay tuned for this exciting feature!
              </p>
            </motion.div>
            
            {/* Disabled Microphone Icon */}
            <div className="flex justify-center mb-6">
              <motion.div
                className="p-6 rounded-full bg-glass-light border border-glass-medium"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <FiMic size={32} className="text-gray-500" />
              </motion.div>
            </div>
            
            {/* Instructions */}
            <div className="text-sm text-gray-400">
              <p>For now, continue your conversation with Lyo using text chat</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default VoiceChatModal